// Frontend types for the SRS (spaced repetition) feature. API DTOs live in
// endchess-contracts; this file keeps UI-only review hub/session types.

import type {
  CardSource,
  MoveFeedbackDto,
  SrsDeck,
  TrainSide,
} from 'endchess-contracts';

export type {
  CardSource,
  CardStatus,
  SrsDeck,
  TrainSide,
  SrsStatsDto,
  ReviewCardDto,
  GradeReviewResultDto,
  LineListItemDto,
  LineDetailDto,
  LineDrillMove,
  LineDrillResultDto,
  CardManageItemDto,
  CourseManageCardsDto,
  CourseManageItemDto,
  ManageSummaryDto,
  BulkDeleteResultDto,
} from 'endchess-contracts';

// UI-facing review categories shown in the hub, footer chips, and sessions.
// - `courses` -> position deck cards enrolled from course lessons
// - `games`   -> position deck cards from replay / game training
// - `puzzles` -> puzzle deck (full puzzle solutions)
// - `lines`   -> drillable move sequences (position deck)
// - `all`     -> combined session covering everything above
export type ReviewCategory =
  | 'all'
  | 'courses'
  | 'games'
  | 'other'
  | 'puzzles'
  | 'lines';

/** Session footer chips and hub sections (excludes the combined `all` session). */
export const REVIEW_QUEUE_CATEGORIES: Exclude<ReviewCategory, 'all'>[] = [
  'courses',
  'games',
  'puzzles',
  'lines',
  'other',
];

export const REVIEW_CATEGORIES: ReviewCategory[] = [
  'all',
  ...REVIEW_QUEUE_CATEGORIES,
];

export const REVIEW_CATEGORY_LABELS: Record<ReviewCategory, string> = {
  all: 'All',
  courses: 'Courses',
  games: 'Games',
  other: 'Other',
  puzzles: 'Puzzles',
  lines: 'Lines',
};

export const isReviewCategory = (value: string): value is ReviewCategory =>
  value === 'all' ||
  value === 'courses' ||
  value === 'games' ||
  value === 'other' ||
  value === 'puzzles' ||
  value === 'lines';

/** Legacy `/review/moves` URLs from before courses/games split. */
export const isLegacyReviewCategory = (value: string): boolean =>
  value === 'moves';

// Maps a reviewable category onto its backend deck. `lines` review is sourced
// from the position deck but driven through the lines endpoints.
export const deckForCategory = (category: ReviewCategory): SrsDeck =>
  category === 'puzzles' ? 'puzzle' : 'position';

// Number of items pending per category. `all` is the sum of the others.
export type ReviewCounts = {
  all: number;
  courses: number;
  games: number;
  puzzles: number;
  lines: number;
  /** Manual or untagged position cards without course or game metadata. */
  other: number;
};

export const EMPTY_REVIEW_COUNTS: ReviewCounts = {
  all: 0,
  courses: 0,
  games: 0,
  puzzles: 0,
  lines: 0,
  other: 0,
};

/** GET /srs/counts — nav badges plus per-course review counts. */
export type SrsCountsDto = ReviewCounts & {
  moveDueByCourseSlug: Record<string, number>;
  lineReviewByCourseSlug: Record<string, number>;
};

export const EMPTY_SRS_COUNTS: SrsCountsDto = {
  ...EMPTY_REVIEW_COUNTS,
  moveDueByCourseSlug: {},
  lineReviewByCourseSlug: {},
};

export type CourseDrillScope = 'line' | 'move';

// A due card from GET /srs/review?deck=... (answer withheld for position cards).
export type PositionReviewCard = {
  cardId: string;
  kind: 'position';
  /** Puzzle-ready start FEN (opponent to move when a setup move exists). */
  FEN: string;
  /** Space-separated UCI: optional opponent setup move, then the answer. */
  Moves: string;
  /** Raw card position with the trainer to move. */
  fen: string;
  sideToMove: TrainSide;
  expectedUci?: string;
  expectedSan?: string;
  /** Source-game players, when the position was enrolled from a game replay. */
  white?: string;
  black?: string;
  whiteElo?: number;
  blackElo?: number;
  timeControl?: string;
  timeClass?: string;
  /** PGN UTCDate from the source game (e.g. "2024.01.15"). */
  date?: string;
  result?: string;
  eco?: string;
  opening?: string;
  /** Explorer game rows use `event` for the PGN opening name. */
  event?: string;
  openingSans?: string[];
  /** How the position was enrolled (`auto` replay/lesson miss, `line` course/explorer line, `manual`). */
  source?: CardSource;
  courseSlug?: string;
  courseTitle?: string;
  lessonId?: string;
  lessonTitle?: string;
  interval: number;
  dueAt: string;
};

export type PuzzleReviewCard = {
  cardId: string;
  kind: 'puzzle';
  puzzleId: string;
  FEN: string;
  Moves: string;
  Rating?: number;
  Themes?: string;
  OpeningTags?: string;
  /** First solution ply to quiz (missed-puzzle review). */
  reviewStartIndex?: number;
  /** Exclusive end of this card's autoplay segment. */
  reviewEndIndex?: number;
  /** Solution plies the user must find during review. */
  quizAtIndices?: number[];
  interval: number;
  dueAt: string;
};

// Body for POST /srs/review/:cardId. Provide one of these; the backend resolves
// it into an SM-2 grade.
export type GradeReviewBody = Partial<MoveFeedbackDto> & {
  /** Explicit SM-2 grade 0-5. */
  grade?: number;
  /** Position decks: the move the user played, compared to the card answer. */
  playedUci?: string;
  /** Puzzle decks: outcome of the attempt. */
  result?: 'solved' | 'hinted' | 'failed';
};

export type ManageTab = 'all' | 'moves' | 'puzzles' | 'lines' | 'courses';
